/**
 * @file ConsentPreviewSettings.tsx
 * @description Образец окна «Настроить» баннера cookie (phase19.16, Р3г;
 * uiux-r3g-consent-texts §3): заголовок, «Необходимые» с подписью «Всегда
 * включены», категории сервисов образца с описанием (у «Аналитики» при
 * Вебвизоре — своё описание) и кнопка «Сохранить выбор». Нейтральные цвета.
 */

import type { ConsentBannerTexts } from "@chaiberi/shared";
import type { ReactNode } from "react";

import type { PreviewCategory } from "../../../lib/platform/consent-preview";

interface Props {
  texts: ConsentBannerTexts;
  categories: readonly PreviewCategory[];
  webvisor: boolean;
}

/** Переключатель образца — картинка, не элемент управления. */
function SampleSwitch() {
  return (
    <span aria-hidden className="relative inline-flex h-6 w-10 shrink-0 rounded-full bg-outline-variant">
      <span className="absolute left-1 top-1 size-4 rounded-full bg-surface-lowest" />
    </span>
  );
}

function Row({ label, description, services, aside }: { label: string; description: string; services?: string; aside: ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-3 [&+&]:[box-shadow:inset_0_1px_0_var(--outline-variant)]">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-xs text-on-surface-variant">{description}</span>
        {services ? <span className="text-xs text-on-surface-variant">{services}</span> : null}
      </div>
      {aside}
    </div>
  );
}

export function ConsentPreviewSettings({ texts, categories, webvisor }: Props) {
  return (
    <div className="flex justify-center p-4">
      <div
        data-preview-form="settings"
        className="flex w-full max-w-[28rem] flex-col gap-2 rounded-2xl bg-surface-lowest p-4 text-on-surface [box-shadow:inset_0_0_0_1px_var(--outline-variant)]"
      >
        <p className="m-0 text-base font-semibold">{texts.settingsTitle}</p>
        <div className="flex flex-col">
          <Row
            label={texts.necessary.label}
            description={texts.necessary.description}
            aside={<span className="shrink-0 text-xs text-on-surface-variant">{texts.alwaysOn}</span>}
          />
          {categories.map((c) => (
            <Row
              key={c.id}
              label={texts.categories[c.id].label}
              description={
                c.id === "analytics" && webvisor ? texts.analyticsWebvisorDescription : texts.categories[c.id].description
              }
              services={c.services.join(", ")}
              aside={<SampleSwitch />}
            />
          ))}
        </div>
        <span className="inline-flex min-h-12 items-center justify-center rounded-lg bg-on-surface px-4 text-sm text-surface-lowest">
          {texts.buttons.save}
        </span>
      </div>
    </div>
  );
}
